import { doc, setDoc, getDoc, runTransaction } from 'firebase/firestore';
import { db } from '../firebase';
import { encryptData, decryptData } from './encryption';
import { logError } from './logger';

/**
 * Derives an encryption key from a user's password using PBKDF2.
 * 
 * PBKDF2 (Password-Based Key Derivation Function 2) is the industry standard
 * for deriving cryptographic keys from passwords. This function uses:
 * - 100,000 iterations (recommended for 2024+)
 * - SHA-256 hash function
 * - 16-byte random salt (generated if not provided)
 * - 256-bit output key length
 * 
 * Security considerations:
 * - High iteration count makes brute-force attacks computationally expensive
 * - Unique salt per user prevents rainbow table attacks
 * - Salt must be stored securely and retrieved for decryption
 * 
 * @param {string} password - User's password for key derivation
 * @param {string|null} salt - Base64-encoded salt (16 bytes). If null, generates new salt.
 * @returns {Promise<CryptoKey>} Derived AES-256-GCM key for encryption/decryption
 * 
 * @example
 * // For new key (generate salt)
 * const key = await deriveKeyFromPassword('userPassword');
 * 
 * // For existing key (use stored salt)
 * const key = await deriveKeyFromPassword('userPassword', storedSalt);
 */
async function deriveKeyFromPassword(password, salt = null) {
  const encoder = new TextEncoder();
  const passwordData = encoder.encode(password);
  
  // Generate a salt if one doesn't exist (for new keys)
  // For existing keys, we need to retrieve the salt from storage
  let saltBuffer;
  if (salt) {
    // Convert base64 salt back to ArrayBuffer
    saltBuffer = Uint8Array.from(atob(salt), c => c.charCodeAt(0)).buffer;
  } else {
    // Generate a new random salt (16 bytes)
    saltBuffer = crypto.getRandomValues(new Uint8Array(16)).buffer;
  }
  
  // Import the password as a key for PBKDF2
  const passwordKey = await crypto.subtle.importKey(
    'raw',
    passwordData,
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );
  
  // Derive key using PBKDF2 with recommended parameters
  // 100,000 iterations is a good balance between security and performance
  const iterations = 100000;
  const keyLength = 256;
  
  return await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBuffer,
      iterations: iterations,
      hash: 'SHA-256'
    },
    passwordKey,
    { name: 'AES-GCM', length: keyLength },
    true,
    ['encrypt', 'decrypt']
  );
}

/**
 * Stores an encrypted master encryption key in Firestore for cloud backup.
 * 
 * The master key is encrypted using a password-derived key (PBKDF2) before storage.
 * A new random salt is generated and stored with the encrypted key for future decryption.
 * 
 * Security flow:
 * 1. Generate random 16-byte salt
 * 2. Derive key from password using PBKDF2 with salt
 * 3. Export master key to raw format
 * 4. Encrypt master key with password-derived key
 * 5. Store encrypted key + salt in Firestore
 * 
 * @param {string} userId - Firebase user ID
 * @param {CryptoKey} encryptionKey - Master encryption key to store
 * @param {string} password - User's password for encrypting the master key
 * @returns {Promise<boolean>} True if storage successful, false otherwise
 * 
 * @example
 * const success = await storeKeyInCloud(user.uid, masterKey, 'userPassword');
 * if (success) {
 *   console.log('Key backed up to cloud');
 * }
 */
export async function storeKeyInCloud(userId, encryptionKey, password) {
  try {
    // Generate a new salt for this key
    const saltArray = crypto.getRandomValues(new Uint8Array(16));
    const saltBase64 = btoa(String.fromCharCode(...saltArray));
    
    // Derive a key from the password using the salt
    const passwordKey = await deriveKeyFromPassword(password, saltBase64);
    
    // Export the encryption key to raw format
    const exportedKey = await crypto.subtle.exportKey('raw', encryptionKey);
    
    // Convert ArrayBuffer to Uint8Array for encryption
    const keyData = new Uint8Array(exportedKey);
    
    // Encrypt the encryption key with the password-derived key
    const encryptedKey = await encryptData(
      Array.from(keyData).join(','),  // Convert to string format
      passwordKey
    );
    
    // Store the encrypted key and salt in Firebase
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      encryptedKey: encryptedKey,
      keySalt: saltBase64, // Store salt for key derivation
      updatedAt: new Date()
    }, { merge: true });
    
    return true;
  } catch (error) {
    logError('Error storing key in cloud', error);
    return false;
  }
}

const encryptedKeyRecord = async (encryptionKey, password) => {
  const saltArray = crypto.getRandomValues(new Uint8Array(16));
  const keySalt = btoa(String.fromCharCode(...saltArray));
  const passwordKey = await deriveKeyFromPassword(password, keySalt);
  const raw = new Uint8Array(await crypto.subtle.exportKey('raw', encryptionKey));
  const encryptedKey = await encryptData(Array.from(raw).join(','), passwordKey);
  return { encryptedKey, keySalt, updatedAt: new Date() };
};

export const createCloudKeyBackup = ({ transaction = runTransaction, firestore = db, document = doc, makeRecord = encryptedKeyRecord, load = getKeyFromCloud, exportKey = (key) => crypto.subtle.exportKey('raw', key) } = {}) => {
  const exportBytes = async (key) => new Uint8Array(await exportKey(key));
  const keysEqual = async (left, right) => {
    try { const [a, b] = await Promise.all([exportBytes(left), exportBytes(right)]); return a.length === b.length && a.every((value, index) => value === b[index]); } catch (_) { return false; }
  };
  const writeIfAbsent = async (userId, key, password) => {
    try {
      const ref = document(firestore, 'users', userId);
      return await transaction(firestore, async (tx) => {
        const existing = await tx.get(ref);
        if (existing.exists() && existing.data().encryptedKey) return false;
        const record = await makeRecord(key, password);
        tx.set(ref, record, { merge: true });
        return true;
      });
    } catch (_) { return false; }
  };
  const storeAndVerify = async (userId, key, password) => {
    try { await writeIfAbsent(userId, key, password); const cloud = await load(userId, password); return !!cloud && await keysEqual(key, cloud); } catch (_) { return false; }
  };
  return { exportBytes, keysEqual, writeIfAbsent, storeAndVerify };
};

/**
 * Retrieves and decrypts the master encryption key from Firestore.
 * 
 * The function supports both new PBKDF2-encrypted keys and legacy SHA-256 keys
 * for backward compatibility. It retrieves the encrypted key and salt from Firestore,
 * derives the password key, and decrypts the master key.
 * 
 * Security considerations:
 * - Returns null if key doesn't exist or password is incorrect
 * - Supports legacy SHA-256 keys for backward compatibility
 * - Throws error if decryption fails (wrong password, corrupted data)
 * 
 * @param {string} userId - Firebase user ID
 * @param {string} password - User's password for decrypting the master key
 * @returns {Promise<CryptoKey|null>} Decrypted master key, or null if not found/incorrect password
 * 
 * @example
 * const masterKey = await getKeyFromCloud(user.uid, 'userPassword');
 * if (masterKey) {
 *   // Password correct, key retrieved
 *   const decrypted = await decryptData(encryptedContent, masterKey);
 * }
 */
export const createCloudKeyLoader = ({ readProfile = async (userId) => getDoc(doc(db, 'users', userId)), decrypt = decryptData, importKey = (...args) => crypto.subtle.importKey(...args) } = {}) => {
  const load = async (userId, password) => {
    let userDoc;
    try {
      userDoc = await readProfile(userId);
    } catch (error) {
      logError('Error reading cloud key from Firestore', error);
      throw new Error('Unable to read cloud encryption backup.');
    }
    if (!userDoc.exists() || !userDoc.data().encryptedKey) return null;
    const userData = userDoc.data();
    let passwordKey;
    try {
      if (!userData.keySalt) {
        const data = new TextEncoder().encode(password);
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        passwordKey = await importKey('raw', hashBuffer, { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
      } else {
        passwordKey = await deriveKeyFromPassword(password, userData.keySalt);
      }
    } catch (error) {
      logError('Invalid cloud key record', error);
      throw new Error('Cloud encryption backup is unavailable.');
    }
    let decryptedString;
    try {
      decryptedString = await decrypt(userData.encryptedKey, passwordKey);
    } catch (_) { return null; }
    if (!decryptedString) return null;
    try {
      const keyData = new Uint8Array(decryptedString.split(',').map(Number));
      return await importKey('raw', keyData, { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
    } catch (error) {
      logError('Invalid decrypted cloud key', error);
      throw new Error('Cloud encryption backup is unavailable.');
    }
  };
  return { load };
};

export async function getKeyFromCloud(userId, password) {
  return createCloudKeyLoader().load(userId, password);
}

export const createCloudKeyVerification = ({ store = storeKeyInCloud, load = getKeyFromCloud, exportKey = (key) => crypto.subtle.exportKey('raw', key) } = {}) => {
  const exportBytes = async (key) => new Uint8Array(await exportKey(key));
  const keysEqual = async (left, right) => {
    try { const [a, b] = await Promise.all([exportBytes(left), exportBytes(right)]); return a.length === b.length && a.every((value, index) => value === b[index]); } catch (_) { return false; }
  };
  const verify = async (userId, key, password) => {
    try { const cloudKey = await load(userId, password); return !!cloudKey && await keysEqual(key, cloudKey); } catch (_) { return false; }
  };
  const storeAndVerify = async (userId, key, password) => {
    try { return await store(userId, key, password) && await verify(userId, key, password); } catch (_) { return false; }
  };
  return { exportBytes, keysEqual, verify, storeAndVerify };
};

export async function exportCryptoKeyBytes(key) { return createCloudKeyVerification().exportBytes(key); }
export async function cryptoKeysEqual(left, right) { return createCloudKeyVerification().keysEqual(left, right); }
export async function verifyCloudKeyMatches(userId, key, password) { return createCloudKeyVerification().verify(userId, key, password); }
export async function storeAndVerifyKeyInCloud(userId, key, password) { return createCloudKeyBackup().storeAndVerify(userId, key, password); }

export const createCloudKeyPasswordChange = ({ load = getKeyFromCloud, store = storeKeyInCloud, keysEqual = cryptoKeysEqual } = {}) => {
  const change = async (userId, localKey, oldPassword, newPassword) => {
    try {
      const cloudKey = await load(userId, oldPassword);
      if (!cloudKey || !await keysEqual(localKey, cloudKey)) return false;
      return Boolean(await store(userId, localKey, newPassword));
    } catch (_) { return false; }
  };
  return { change };
};

/**
 * Checks if a cloud-stored encryption key exists for the user.
 * 
 * This is a lightweight check that doesn't require password authentication.
 * Useful for determining if user should be prompted to load their cloud key.
 * 
 * @param {string} userId - Firebase user ID
 * @returns {Promise<boolean>} True if cloud key exists, false otherwise
 * 
 * @example
 * const hasCloudKey = await checkCloudKeyExists(user.uid);
 * if (hasCloudKey && !localKey) {
 *   // Prompt user to load from cloud
 * }
 */
export async function checkCloudKeyExists(userId) {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    return userDoc.exists() && userDoc.data().encryptedKey ? true : false;
  } catch (error) {
    logError('Error checking cloud key', error);
    return false;
  }
}

/**
 * Changes the password used to encrypt the cloud-stored master key.
 * 
 * The function verifies the old password by attempting to decrypt the existing key,
 * byte-compares it with the local master key, then re-encrypts that same key with
 * the new password and stores it in Firestore.
 * 
 * Security considerations:
 * - Verifies the old password and requires the cloud key to match the local key before allowing change
 * - Generates new salt for the new password
 * - Master key remains the same, only encryption password changes
 * 
 * @param {string} userId - Firebase user ID
 * @param {CryptoKey} key - Current master encryption key
 * @param {string} oldPassword - Current password for verification
 * @param {string} newPassword - New password for encrypting the key
 * @returns {Promise<boolean>} True if password change successful, false otherwise
 * @throws {Error} If old password is incorrect
 * 
 * @example
 * const success = await changeKeyPassword(
 *   user.uid,
 *   masterKey,
 *   'oldPassword',
 *   'newPassword'
 * );
 */
export async function changeKeyPassword(userId, key, oldPassword, newPassword) {
  return createCloudKeyPasswordChange().change(userId, key, oldPassword, newPassword);
}
